import { saveAs } from "file-saver";

// Colour-coded XLSX export of the transfers table. exceljs is loaded lazily
// (dynamic import) so it doesn't bloat the main bundle — it's only pulled in
// when someone actually exports.
//
// Colour key:
//   Direction   IN = blue (arrivals), OUT = orange (departures)
//   UM = Yes    pale yellow (unaccompanied minor)
//   Transport   no group = red, assigned+confirmed = green, assigned = amber
const COLOURS = {
  header: "FF305496",
  in: "FFDDEBF7",
  out: "FFFCE4D6",
  um: "FFFFF2CC",
  unassigned: "FFFFC7CE",
  confirmed: "FFC6EFCE",
  planned: "FFFFEB9C",
  // Row banding, alternated per flight.
  bandWhite: "FFFFFFFF",
  bandGrey: "FFF2F2F2",
};

// Identifies one flight so consecutive passengers on it share a row band.
const flightKey = (t, time) =>
  [t.direction, t.flightId, time, t.departing, t.destination]
    .map((x) => String(x ?? "").trim())
    .join("|");

const fill = (argb) => ({
  type: "pattern",
  pattern: "solid",
  fgColor: { argb },
});

const isAdultRow = (t) =>
  String(t?.personType || "").toUpperCase() === "ADULT" ||
  (t?.adultId && Number(t.adultId) !== 0);

const isConfirmedGroup = (g) =>
  !!g &&
  (String(g.status || "").trim().toLowerCase() === "confirmed" ||
    Boolean(g.confirmedAt));

const stopTime = (val) => {
  const s = String(val || "").trim();
  if (/^\d{2}:\d{2}:\d{2}$/.test(s)) return s.slice(0, 5);
  if (/^\d{2}:\d{2}$/.test(s)) return s;
  return "";
};

// "AMS 14:30 (pickup) → EDI 16:10 (dropoff)" for the group's route.
const formatStops = (group) => {
  const stops = Array.isArray(group?.stops) ? group.stops.slice() : [];
  if (!stops.length) return "";
  return stops
    .sort((a, b) => (a.stopOrder ?? 0) - (b.stopOrder ?? 0))
    .map((s) => {
      const loc = String(s.location || "").trim() || "?";
      const t = stopTime(s.plannedTime);
      const type = s.stopType ? ` (${String(s.stopType).toLowerCase()})` : "";
      return `${loc}${t ? ` ${t}` : ""}${type}`;
    })
    .join("  →  ");
};

export async function downloadTransfersXlsx({
  sortedTransfers,
  transportGroups,
  selectedDate,
}) {
  const { default: ExcelJS } = await import("exceljs");

  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet("Transfers");

  ws.columns = [
    { header: "ID", key: "id", width: 10 },
    { header: "Direction", key: "direction", width: 11 },
    { header: "Name", key: "name", width: 26 },
    { header: "Campus", key: "campus", width: 16 },
    { header: "Flight", key: "flight", width: 12 },
    { header: "From", key: "from", width: 20 },
    { header: "To", key: "to", width: 20 },
    { header: "Time", key: "time", width: 9 },
    { header: "UM", key: "um", width: 6 },
    { header: "Notes", key: "notes", width: 32 },
    { header: "Transport Group", key: "group", width: 26 },
    { header: "Provider", key: "provider", width: 20 },
    { header: "Route (stops & times)", key: "route", width: 48 },
  ];

  const headerRow = ws.getRow(1);
  headerRow.font = { bold: true, color: { argb: "FFFFFFFF" } };
  headerRow.fill = fill(COLOURS.header);
  headerRow.alignment = { vertical: "middle" };
  ws.views = [{ state: "frozen", ySplit: 1 }];
  ws.autoFilter = { from: "A1", to: "M1" };

  // Row banding alternates per flight: every row of a flight shares a shade,
  // and the shade flips when the flight changes (first flight = white).
  let prevKey = null;
  let bandGrey = false;

  (sortedTransfers || []).forEach((t) => {
    const adult = isAdultRow(t);
    const time = t.direction === "IN" ? t.arrivalTime : t.departureTime;
    const group = (transportGroups || []).find((g) =>
      adult ? g.adultIds?.includes(t.adultId) : g.studentIds?.includes(t.studentId)
    );
    const groupName = group?.groupName || "";
    // Self-transfer (the "private pickup" tick) is recorded as flightId
    // SELFTRANSFER and needs no bus — it reads as confirmed, same as the main
    // page, not "unassigned".
    const isSelfTransfer =
      String(t.flightId || "").trim().toUpperCase() === "SELFTRANSFER";

    const key = flightKey(t, time);
    if (prevKey !== null && key !== prevKey) bandGrey = !bandGrey;
    prevKey = key;

    const row = ws.addRow({
      id: adult ? `A${t.adultId}` : t.studentId,
      direction: t.direction || "",
      name: t.name || "",
      campus: t.campus || "",
      flight: t.flightId || "",
      from: t.departing || "",
      to: t.destination || "",
      time: time || "",
      um: t.um ? "Yes" : "No",
      notes: (t.notes || "").replace(/\n/g, " "),
      group: groupName || (isSelfTransfer ? "Self-transfer" : "— unassigned —"),
      provider: group?.provider || "",
      route: formatStops(group),
    });

    // Per-flight row band first, then the cell colours overlay on top.
    const band = fill(bandGrey ? COLOURS.bandGrey : COLOURS.bandWhite);
    row.eachCell((cell) => {
      cell.fill = band;
    });

    row.getCell("direction").fill = fill(
      String(t.direction).toUpperCase() === "IN" ? COLOURS.in : COLOURS.out
    );

    if (t.um) {
      const umCell = row.getCell("um");
      umCell.fill = fill(COLOURS.um);
      umCell.font = { bold: true };
    }

    const groupCell = row.getCell("group");
    if (groupName) {
      groupCell.fill = fill(isConfirmedGroup(group) ? COLOURS.confirmed : COLOURS.planned);
    } else if (isSelfTransfer) {
      // No bus needed — reads as done (green), like the main page.
      groupCell.fill = fill(COLOURS.confirmed);
    } else {
      groupCell.fill = fill(COLOURS.unassigned);
      groupCell.font = { italic: true };
    }
  });

  const buffer = await wb.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  saveAs(blob, `Transfers_${selectedDate.format("YYYY-MM-DD")}.xlsx`);
}
